import {
  Component,
  OnInit,
  EventEmitter,
  Input,
  Output,
  OnChanges,
  SimpleChanges,
} from '@angular/core';

import { GlobalAddService } from '@app/global-add/services/global-add.service';
import { GlobalAddInputAction } from '@app/global-add/global-add.model';
import { LearningResourceViewModel } from '@app/inputs/models/learning-resource.view-model';
import { PathwayAddByTypeComponent } from '@app/pathways/components/pathway/pathway-add-by-type/pathway-add-by-type.component';
import { AddModalResults } from '@app/pathways/pathway-api.model';
import { AddResourcesModel } from '../../services/add-resources-modal.service';
import { TranslateService } from '@ngx-translate/core';
import { InputType } from '@app/shared/models/core-api.model';
import { InputIdentifier } from '@app/inputs/inputs-api.model';
import { InputModalDispatcherService } from '@app/user-content/user-input/services/input-modal-dispatcher.service';
import { InputsService } from '@app/inputs/services/inputs.service';
import { AuthService } from '@app/shared/services/auth.service';
import { Target } from '@app/target/target-api.model';
import { NotifierService } from '@app/shared/services/notifier.service';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { LDFlagsService } from '@app/shared/services/ld-flags.service';
import { InputAddType } from '@app/inputs/inputs.model';

@Component({
  selector: 'dgx-target-add-by-type',
  templateUrl: './target-add-by-type.component.html',
  styleUrls: ['./target-add-by-type.component.scss'],
})
export class TargetAddByTypeComponent implements OnInit, OnChanges {
  public inputTypes: Partial<GlobalAddInputAction>[];
  public savedItems: LearningResourceViewModel[] = [];
  public isLoading: boolean;
  public loadingContentItems = false;
  public addItemsButton: string;
  public orgManagedLDFlag = this.ldFlagsService.orgManagedLDFlag;

  public i18n = this.translate.instant(['Pathways_ExistingContentTypeError']);

  @Input() public savedSearch: LearningResourceViewModel[];
  @Input() public isSubmitting: boolean;
  @Input() public addResourcesModel: AddResourcesModel;
  @Input() public target: Target;

  @Output() public addItems = new EventEmitter<AddModalResults>();
  @Output() public updateItemList = new EventEmitter<AddModalResults>();
  @Output() public addToBin = new EventEmitter<AddModalResults>();
  @Output() public dismiss = new EventEmitter<Event>();

  constructor(
    private activeModal: NgbActiveModal,
    private authService: AuthService,
    private inputsService: InputsService,
    private genericInputModalService: InputModalDispatcherService,
    private globalAddService: GlobalAddService,
    private notifierService: NotifierService,
    private translate: TranslateService,
    private ldFlagsService: LDFlagsService
  ) {}

  public get showOrgManagedNotification() {
    const savedItems = this.savedItems as any;
    return (
      !this.target.organizationId &&
      this.orgManagedLDFlag &&
      savedItems.some((item) => item.isCmsContent)
    );
  }

  public ngOnInit(): void {
    this.updateButtonText();
    this.inputTypes = this.globalAddService.getTargetAddInputs();
    // set the current state for already selected items.
    this.savedItems = this.savedSearch ?? [];
  }

  public ngOnChanges({ savedSearch }: SimpleChanges): void {
    if (
      savedSearch &&
      this.savedSearch &&
      this.savedItems !== this.savedSearch
    ) {
      this.savedItems = this.savedSearch;
    }
  }

  public onAddItems(): void {
    this.addItems.emit({
      savedItems: this.savedItems,
      selectedSearch: this.savedSearch,
      whichModal: PathwayAddByTypeComponent.modalName,
    });
  }

  public showAddInputForm(e: MouseEvent, inputType: InputAddType): void {
    this.genericInputModalService
      .showModal({
        inputType: inputType as InputType,
        organizationId: this.target.organizationId,
        sourceTarget: e.target as HTMLElement,
        targetId: this.target.targetId,
        isCompleting: false,
      })
      .subscribe({
        next: (item: any) => {
          if (item) {
            // for some reason this sometimes returns a number instead of string. Add the type we originally passed in to the modal to fix.
            item.inputType = inputType;
            this.handleAddContentItem(item);
          }
        },
        complete: () => {
          (e.target as HTMLButtonElement).focus();
        },
      });
  }

  public removeFromSavedList = (item: LearningResourceViewModel): void => {
    let savedItemList: LearningResourceViewModel[];
    savedItemList = this.savedItems;

    if (item) {
      savedItemList = savedItemList.filter((i) => {
        return i.resourceId !== item.resourceId;
      });
    }
    this.updateButtonText(true);
    this.updateItemList.emit({
      savedItems: savedItemList,
      whichModal: PathwayAddByTypeComponent.modalName,
    });
  };

  public addToSavedList(item: LearningResourceViewModel): void {
    let savedItemList: LearningResourceViewModel[];
    if (this.savedItems) {
      savedItemList = this.savedItems;
      savedItemList.push(item);
    } else {
      savedItemList = [item];
    }
    this.updateButtonText();
    this.updateItemList.emit({
      savedItems: savedItemList,
      whichModal: PathwayAddByTypeComponent.modalName,
    });
  }

  public handleAddContentItem = (item): void => {
    const inputIdentifier: InputIdentifier = {
      inputType: item.inputType,
      inputId: item.inputId,
    };
    this.inputsService.getInputAndStatistics(inputIdentifier).subscribe({
      next: (result) => {
        if (item.alreadyExists && !result.input) {
          // Prevents user from adding the same content under a different type
          this.notifierService.showError(
            this.i18n.Pathways_ExistingContentTypeError
          );
          this.activeModal.close();
        } else {
          const input: any = result.input;
          this.loadingContentItems = false;
          this.addToSavedList(input);

          // TODO: Add tracking?
        }
      },
    });
  };

  private updateButtonText(remove?: boolean) {
    // if user removes an item that was added in another tab,
    // then -1 to display the correct number in the button
    const count = remove
      ? this.savedItems?.length - 1
      : this.savedItems?.length;
    let text: string;
    if (count > 1) {
      text = this.translate.instant('Pathways_AddXItems', {
        count,
      });
    } else if (count === 1) {
      text = this.translate.instant('Pathways_AddOneItem');
    } else {
      text = this.translate.instant('Pathways_AddXItems', {
        count: '',
      });
    }
    this.addItemsButton = text;
  }
}
